import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Trash2, AlertCircle } from 'lucide-react';
import { GMUD, Aprovador, CorrecaoGMUD, CreateGMUDData } from '../types';
import api from '../services/api';
import { mensagemDeErro } from '../utils/erroApi';
import GMUDForm, { ArquivosPendentes } from '../components/gmud-form/GMUDForm';
import GmudIdBadge from '../components/GmudIdBadge';

const GMUDEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [gmud, setGmud] = useState<GMUD | null>(null);
  const [aprovadores, setAprovadores] = useState<Aprovador[]>([]);
  const [correcao, setCorrecao] = useState<CorrecaoGMUD | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gmudResponse, aprovadoresResponse] = await Promise.all([
          api.get<GMUD>(`/gmuds/${id}`),
          api.get<Aprovador[]>('/aprovadores'),
        ]);

        const gmudData = gmudResponse.data;

        // Verificar se GMUD pode ser editado
        if (gmudData.status === 'concluido' || gmudData.status === 'cancelado') {
          toast.error(`GMUD com status "${gmudData.status}" não pode ser editado`);
          navigate(`/gmuds/${id}`);
          return;
        }

        // Em correção, mostra o pedido em aberto para orientar a edição
        if (gmudData.status === 'correcao_solicitada') {
          try {
            const correcoes = await api.get<CorrecaoGMUD[]>(`/gmuds/${id}/correcoes`);
            const aberta = [...correcoes.data].reverse().find(item => item.situacao === 'aberta');
            setCorrecao(aberta || null);
          } catch (error) {
            console.error('Erro ao carregar correção solicitada:', error);
          }
        }

        setGmud(gmudData);
        setAprovadores(aprovadoresResponse.data);
      } catch (error) {
        toast.error('Erro ao carregar dados do GMUD');
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, navigate]);

  const onSalvar = async (dados: CreateGMUDData, _arquivos: ArquivosPendentes, enviar: boolean) => {
    if (!id) return;

    setSaving(true);
    let salvo = false;
    try {
      await api.put(`/gmuds/${id}`, dados);
      salvo = true;

      if (enviar) {
        await api.patch(`/gmuds/${id}/status`, { status: 'pendente' });
        toast.success('GMUD atualizada e enviada para aprovação!');
        navigate(`/gmuds/${id}`);
        return;
      }

      toast.success('GMUD atualizada com sucesso!');
      // Em correção, o próximo passo (reenviar para execução) fica no detalhe
      navigate(gmud?.status === 'correcao_solicitada' ? `/gmuds/${id}` : '/gmuds');
    } catch (error: unknown) {
      if (salvo) {
        toast.error(
          `Alterações salvas, mas a GMUD não foi enviada: ${mensagemDeErro(
            error,
            'erro ao enviar para aprovação'
          )}`,
          { duration: 8000 }
        );
      } else {
        toast.error(mensagemDeErro(error, 'Erro ao atualizar GMUD'));
      }
      console.error('Erro:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Tem certeza que deseja excluir este GMUD?')) return;

    try {
      await api.delete(`/gmuds/${id}`);
      toast.success('GMUD excluído com sucesso!');
      navigate('/gmuds');
    } catch (error) {
      toast.error('Erro ao excluir GMUD');
      console.error('Erro:', error);
    }
  };

  if (loading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!gmud) {
    return (
      <div className='flex min-h-[50vh] items-center justify-center'>
        <div className='text-center'>
          <AlertCircle className='mx-auto mb-4 h-12 w-12 text-red-500' />
          <h2 className='text-xl font-semibold text-gray-900'>GMUD não encontrado</h2>
          <p className='mt-2 text-gray-600'>O GMUD solicitado não foi encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='mx-auto flex max-w-6xl items-center justify-between gap-4'>
        <div className='flex items-center space-x-4'>
          <button
            type='button'
            onClick={() => navigate(`/gmuds/${id}`)}
            className='flex items-center space-x-2 text-gray-600 transition-colors hover:text-gray-900'
          >
            <ArrowLeft className='h-5 w-5' />
            <span>Voltar</span>
          </button>
          <div>
            <div className='mb-1 flex items-center gap-2'>
              <GmudIdBadge id={gmud.id} />
              <span className='text-sm text-gray-500'>Edição</span>
            </div>
            <h1 className='text-3xl font-bold text-gray-900'>Editar GMUD</h1>
          </div>
        </div>

        {gmud.canDelete && (
          <button
            type='button'
            onClick={handleDelete}
            className='flex items-center space-x-2 rounded-lg border border-red-300 px-4 py-2 text-red-700 transition-colors hover:bg-red-50'
          >
            <Trash2 className='h-4 w-4' />
            <span>Excluir</span>
          </button>
        )}
      </div>

      <GMUDForm
        modo='editar'
        gmud={gmud}
        aprovadores={aprovadores}
        correcao={correcao}
        salvando={saving}
        onSalvar={onSalvar}
        onCancelar={() => navigate(`/gmuds/${id}`)}
      />
    </div>
  );
};

export default GMUDEdit;
